import moment from 'moment';

import { GlobalSettings, GlobalSettingsService } from '../../components/services/GlobalSettingsService';
import { CalendarAssignmentEmployee, Shift } from '../../types/interfaces';

export interface ShiftValidationError {
  personId: number;
  employeeName?: string;
  rule: 'MaxMonthlyScheduledHours' | 'MaxConsecutiveDaysWithShifts';
  ruleName: string;
}

export async function getGlobalSettings(): Promise<GlobalSettings> {
  let settingsPromise: Promise<GlobalSettings> | null = null;
  settingsPromise = GlobalSettingsService.get();
  return settingsPromise;
}

function getShiftHours(shift: Shift, splitHours: boolean, monthEnd: moment.Moment): number {
  const [hours, minutes] = shift.workdayDuration.split(':').map(Number);
  const workdayDurationHours = hours + minutes / 60;
  
  const breakDuration = shift.breakDuration || '00:00';
  const [breakHours, breakMinutes] = breakDuration.split(':').map(Number);
  const breakHoursTotal = breakHours + breakMinutes / 60;
  
  let workdayHours: number;
  
  if (splitHours) {
    // Вычисляем начало и конец смены
    const shiftStart = moment(shift.date + ' ' + shift.workdayStart);
    const [durationHours, durationMinutes] = shift.workdayDuration.split(':').map(Number);
    const shiftEnd = shiftStart.clone().add(durationHours, 'hours').add(durationMinutes, 'minutes');
    
    const breakStartOffset = workdayDurationHours / 2 - breakHoursTotal / 2;
    const breakEndOffset = workdayDurationHours / 2 + breakHoursTotal / 2;
    const breakStart = shiftStart.clone().add(breakStartOffset, 'hours');
    const breakEnd = shiftStart.clone().add(breakEndOffset, 'hours');
    
    const monthEndMoment = monthEnd.clone().add(1, 'day').startOf('day');
    
    if (shiftEnd.isBefore(monthEndMoment) || shiftEnd.isSame(monthEndMoment)) {
      workdayHours = workdayDurationHours - breakHoursTotal;
    } else if (shiftStart.isBefore(monthEndMoment)) {
      const totalHoursInMonth = monthEndMoment.diff(shiftStart, 'hours', true);
      
      let breakHoursInMonth = 0;
      
      const breakIntersectionStart = moment.max(breakStart, shiftStart);
      const breakIntersectionEnd = moment.min(breakEnd, monthEndMoment);
      
      if (breakIntersectionEnd.isAfter(breakIntersectionStart)) {
        breakHoursInMonth = breakIntersectionEnd.diff(breakIntersectionStart, 'hours', true);
      }
      
      workdayHours = totalHoursInMonth - breakHoursInMonth;
    } else {
      workdayHours = 0;
    }
  } else {
    workdayHours = workdayDurationHours - breakHoursTotal;
  }
  
  return Math.max(0, workdayHours);
}

function validateMonthlyHours(
  splitHours: boolean,
  personId: number,
  allShifts: Shift[],
  monthStart: moment.Moment,
  maxMonthlyHours: number
): ShiftValidationError | null {
  const monthEnd = monthStart.clone().endOf('month');
  const personShifts = allShifts.filter(
    (shift) =>
      shift.personId === personId &&
      moment(shift.date).isSameOrAfter(monthStart, 'day') &&
      moment(shift.date).isSameOrBefore(monthEnd, 'day')
  );

  const totalHours = personShifts.reduce((sum, shift) => {
    return sum + getShiftHours(shift, splitHours, monthEnd);
  }, 0);

  if (totalHours > maxMonthlyHours) {
    return {
      personId,
      rule: 'MaxMonthlyScheduledHours',
      ruleName: 'Максимальное количество запланированных часов за месяц'
    };
  }

  return null;
}

function validateConsecutiveShifts(
  personId: number,
  allShifts: Shift[],
  maxConsecutiveDays: number
): ShiftValidationError | null {
  const personShifts = allShifts
    .filter((shift) => shift.personId === personId)
    .map((shift) => moment(shift.date))
    .sort((a, b) => a.diff(b));

  if (personShifts.length === 0) {
    return null;
  }

  let maxConsecutive = 1;
  let currentConsecutive = 1;

  for (let i = 1; i < personShifts.length; i++) {
    const daysDiff = personShifts[i].diff(personShifts[i - 1], 'days');
    if (daysDiff === 1) {
      currentConsecutive++;
      maxConsecutive = Math.max(maxConsecutive, currentConsecutive);
    } else {
      currentConsecutive = 1;
    }
  }

  if (maxConsecutive > maxConsecutiveDays) {
    return {
      personId,
      rule: 'MaxConsecutiveDaysWithShifts',
      ruleName: 'Максимальное количество смен подряд'
    };
  }

  return null;
}

export async function validateShifts(
  personId: number,
  allShifts: Shift[],
  monthStart: moment.Moment,
  employees?: CalendarAssignmentEmployee[]
): Promise<ShiftValidationError[]> {
  const errors: ShiftValidationError[] = [];
  
  try {
    const settings = await getGlobalSettings();
    const restrictions = settings.person_shift_restrictions;
    const splitHours = settings.timesheet_split_hours_across_calendar_days;

    // Проверка максимального количества часов за месяц
    const monthlyError = validateMonthlyHours(
      splitHours,
      personId,
      allShifts,
      monthStart,
      restrictions.maxMonthlyScheduledHours
    );
    if (monthlyError) {
      const employee = employees?.find((e) => e.id === personId);
      if (employee) {
        monthlyError.employeeName = employee.name;
      }
      errors.push(monthlyError);
    }

    // Проверка максимального количества смен подряд
    const consecutiveError = validateConsecutiveShifts(
      personId,
      allShifts,
      restrictions.maxConsecutiveDaysWithShifts
    );
    if (consecutiveError) {
      const employee = employees?.find((e) => e.id === personId);
      if (employee) {
        consecutiveError.employeeName = employee.name;
      }
      errors.push(consecutiveError);
    }
  } catch (error) {
    console.error('Ошибка при валидации смен:', error);
  }

  return errors;
}

export async function validateAllShifts(
  allShifts: Shift[],
  monthStart: moment.Moment,
  employees?: CalendarAssignmentEmployee[]
): Promise<ShiftValidationError[]> {
  const personIds = Array.from(new Set(allShifts.map((shift) => shift.personId)));
  const allErrors: ShiftValidationError[] = [];

  for (const personId of personIds) {
    const errors = await validateShifts(personId, allShifts, monthStart, employees);
    allErrors.push(...errors);
  }

  return allErrors;
}

